import { faceDetect, mutiFaceSwap, singleFaceSwap } from "./../api/index";
import { create } from "zustand";
import type {
  TTemplate,
  TTask,
  TSign,
  TTaskOptions,
  TShareOption,
  TSafariInfo,
  TDownloadAdOption,
  TGroup,
  TSwap,
  TMultiFaceSelectOptions,
  TFaceDetection,
  TReuploadOption,
  TServerDeTectFaceData,
  TServerDeTectData,
} from "@/types";
import {
  APICode,
  BASE_CREDIT_CONSUME,
  FACE_CREDIT_CONSUME,
  FACE_LIMIT,
  FaceDetectRes,
  LOOP_INTERVAL,
  LocalStorageKey,
  MAX_LOOP_QUANTITY,
  MOBILE_THUMBNAIL_WIDTH,
  PC_THUMBNAIL_HEIGHT,
  PageType,
  TYPE_LIMIT_ACCEPT,
  TaskStatus,
} from "@/constants";
import { getPresetGroupList, getSignUrl, getTask, putFileTo } from "@/api";
import {
  checkFileSize,
  checkImageSize,
  checkType,
  cropImage,
  expandFace,
  getExtension,
  getRandomId,
  heic2jpeg,
  imageLoader,
  isHeic,
  isTiff,
  selectFile,
  sleep,
  tiff2png,
  zipFile,
} from "@/utils";
import { isEmpty } from "lodash-es";
import { toast } from "react-toastify";
import { t } from "i18next";
import Analyse from "@/utils/analyse";
import { fetchSign, submitFile } from "@/utils/commonFn";
import { getResizeImage } from "@/utils/link";
import { customConfirm } from "@/components/confirm/modal";
import { faceToSwap } from "@/utils/faces";

export const enum FeatureType {
  /**单人换脸 */
  Single = "image",
  /**多人换脸 */
  Multi = "multiple",

  Video = "video",
}

type States = {
  mutiLoading: boolean;
  guidTip: boolean;
  presetGroupList: TGroup[];
  presetMutiGroupList: TGroup[];
  activeGroup: TGroup | null;
  activeMutiGroup: TGroup | null;
  activeTemp: TTemplate | null;
  /** v5 存储新版单人换脸数据 */
  swapTemp: TTemplate | null;
  swapList: TSwap[];
  allPresetVisible: boolean;
  task: TTask | null;
  generating: boolean;
  isMobile: boolean | undefined;
  removeTipVisible: boolean;
  shareOption: TShareOption;
  safariInfo: TSafariInfo | null;
  downloadAdOption: TDownloadAdOption;
  adLoaded: boolean;
  multiFaceSelectOptions: TMultiFaceSelectOptions;
  isDrag: boolean;
  globalLoading: boolean;
  reuploadOption: TReuploadOption;
  errorMsg: string;
  errorCode: string;
  errorDesc: string;

  // 补充
  pageType: PageType;
  featureType: FeatureType;
};

type Getters = {
  getters: {
    activeTemp: TTemplate | null;
    recommendTempList: TTemplate[];
    activeTempUrl?: string;
    isResult: boolean;
    fillSwapList: TSwap[];
    disabledGenerate: boolean;
    swapCheckFaceing: boolean;
    creditConsume: number;
    isSuccess: boolean;
    isFailed: boolean;
    isLimitFace: boolean;
  };
};

type Mutations = {
  setMutiLoading: (value: boolean) => void;
  setGuidTipShow: (value: boolean) => void;
  setTask: (value: TTask | null) => void;
  setGenerating: (value: boolean) => void;
  setIsMobile: (value: boolean | undefined) => void;
  setRemoveTipVisible: (value: boolean) => void;
  setShareOption: (value: TShareOption) => void;
  setSafariInfo: (value: TSafariInfo) => void;
  setDownloadAdOption: (value: TDownloadAdOption) => void;
  setAdLoaded: (value: boolean) => void;
  setAllPresetVisible: (value: boolean) => void;
  setActiveMutiGroup: (value: TGroup) => void;
  setActiveGroup: (value: TGroup) => void;
  setMultiFaceSelectOptions: (value: TMultiFaceSelectOptions) => void;
  setIsDrag: (value: boolean) => void;
  setGlobalLoading: (value: boolean) => void;
  setReuploadOption: (value: TReuploadOption) => void;
  setFeatureType: (value: FeatureType) => void;
  setPageType: (value: PageType) => void;
  setCommonError: (code: string) => void;
  setVoliError: (code: string) => void;
};

type Actions = {
  reset: () => void;
  fetchMutiPresetGroupList: () => Promise<void>;
  fetchPresetGroupList: () => Promise<void>;
  fetchSign: (extension: string) => Promise<TSign>;
  submitFile: (file: File, url: string) => Promise<void>;
  submitTask: (options: any, type: FeatureType) => Promise<TTask>;
  fetchTask: () => Promise<TTask>;
  generate: (inlineFeatureType?: FeatureType) => Promise<void>;
  beforeOperate: (callback?: Function) => void;
  loopTask: () => Promise<void>;
  showRemoveTip: () => void;
  logoutCallback: () => void;
  preApplyPresetTemp: () => void;
  applyPresetTemp: () => void;
  showDownloadAd: () => void;
  chooseTemp: (temp: TTemplate | string | File) => Promise<void>;
  chooseSwapTemp: (temp: TTemplate | string | File) => void;
  clearActiveTemp: () => void;
  clearTask: () => void;
  clearSwapTemp: () => void;
  chooseTarget: (swap: TSwap) => Promise<void>;
  clearTarget: (swap: TSwap) => void;
  uploadFile: (file: File) => Promise<TSign | null>;
};

const initialState: States = {
  errorMsg: "",
  mutiLoading: false,
  guidTip: false,
  presetGroupList: [],
  presetMutiGroupList: [],
  activeGroup: null,
  activeMutiGroup: null,
  activeTemp: null,
  swapTemp: null,
  swapList: [],
  allPresetVisible: false,
  task: null,
  generating: false,
  isMobile: undefined,
  removeTipVisible: false,
  shareOption: {
    show: false,
  },
  safariInfo: null,
  downloadAdOption: {
    show: false,
  },
  adLoaded: false,
  multiFaceSelectOptions: {
    show: false,
  },
  isDrag: false,
  globalLoading: false,
  reuploadOption: {
    show: false,
  },
  pageType: PageType.Ready,
  featureType: FeatureType.Single,
  errorCode: "",
  errorDesc: "",
};

const useCommonStore = create<States & Getters & Mutations & Actions>(
  (set, get) => ({
    // states
    ...initialState,

    // getters
    getters: {
      get activeTemp() {
        return get().activeTemp;
      },
      get recommendTempList() {
        const { presetGroupList, featureType, presetMutiGroupList } = get();
        const isSingle = featureType === FeatureType.Single;
        const isMuti = featureType === FeatureType.Multi;
        const list = isSingle ? presetGroupList : presetMutiGroupList;
        const res: TTemplate[] = [];
        list.forEach((item) => {
          if (isMuti) {
            res.push(...item.tempList.slice(0, 3));
          } else {
            if (!isEmpty(item.tempList)) {
              res.push(item.tempList[0]); // 取每组第一个
            }
          }
        });
        return res;
      },
      get activeTempUrl() {
        const { activeTemp } = get();
        return activeTemp?.resource.url || activeTemp?.resource.blobUrl;
      },
      get isResult() {
        const { task } = get();
        if (task === null) {
          return false;
        }
        return [
          TaskStatus.Failed,
          TaskStatus.FeTimeout,
          TaskStatus.Success,
          TaskStatus.Timeout,
        ].includes(task.status);
      },
      get fillSwapList() {
        const { swapList } = get();
        return swapList.filter((item) => item.source && item.target);
      },
      get swapCheckFaceing() {
        const { swapList, featureType, mutiLoading } = get();
        if (featureType === FeatureType.Single) {
          return false;
        }
        if (featureType === FeatureType.Multi) {
          if (mutiLoading) {
            return true;
          }

          const loading = swapList.some((item) => item.loading);
          if (loading) {
            return true;
          }
        }
        return false;
      },
      get disabledGenerate() {
        const {
          generating,
          activeTemp,
          swapTemp,
          featureType,
          swapList,
          getters: { fillSwapList },
        } = get();
        // if (generating) {
        //   return true;
        // }
        if (!activeTemp) {
          return true;
        }
        if (featureType === FeatureType.Single) {
          if (!swapTemp) return true;
          return false;
        } else if (featureType === FeatureType.Multi) {
          if (fillSwapList.length === 0) {
            return true;
          }
          const loading = swapList.some((item) => item.loading);
          if (loading) {
            return true;
          }
          return false;
        }

        if (fillSwapList.length === 0) {
          return true;
        }
        return false;
      },
      get creditConsume() {
        const {
          getters: { fillSwapList },
          featureType,
        } = get();
        if (featureType === FeatureType.Single) {
          return BASE_CREDIT_CONSUME;
        }
        if (!fillSwapList || fillSwapList.length === 0) {
          return 0;
        }
        return (
          BASE_CREDIT_CONSUME + FACE_CREDIT_CONSUME * (fillSwapList.length - 1)
        );
      },
      get isSuccess() {
        const { task } = get();
        if (!task) {
          return false;
        }
        return (
          task.status === TaskStatus.Success && task.whetherViolations === false
        );
      },
      get isFailed() {
        const { task } = get();
        if (!task) {
          return false;
        }
        return (
          [
            TaskStatus.Failed,
            TaskStatus.Timeout,
            TaskStatus.FeTimeout,
          ].includes(task.status) || task.whetherViolations === true
        );
      },
      get isLimitFace() {
        const {
          getters: { fillSwapList },
        } = get();
        return fillSwapList.length >= FACE_LIMIT;
      },
    },
    setCommonError: (code: string) => {
      set({
        errorCode: code,
        errorDesc: t("error_result_common_desc"),
        errorMsg: t("error_result_common_title"),
      });
    },
    setVoliError: (value) =>
      set({
        errorCode: value,
        errorDesc: t("error_result_vote_desc"),
        errorMsg: t("error_result_vote_title"),
      }),
    setMutiLoading: (value) => set({ mutiLoading: value }),
    setGuidTipShow: (value) => set({ guidTip: value }),
    setPageType: (value) => set({ pageType: value }),
    setFeatureType: (value) => set({ featureType: value }),
    // mutations
    setTask: (value) => set({ task: value }),
    setGenerating: (value) => set({ generating: value }),
    setIsMobile: (value) => set({ isMobile: value }),
    setRemoveTipVisible: (value) => set({ removeTipVisible: value }),
    setShareOption: (value) => set({ shareOption: value }),
    setSafariInfo: (value) => set({ safariInfo: value }),
    setDownloadAdOption: (value) => set({ downloadAdOption: value }),
    setAdLoaded: (value) => set({ adLoaded: value }),
    setAllPresetVisible: (value) => set({ allPresetVisible: value }),
    setActiveGroup: (value) => set({ activeGroup: value }),
    setActiveMutiGroup: (value) => set({ activeMutiGroup: value }),
    setMultiFaceSelectOptions: (value) =>
      set({ multiFaceSelectOptions: value }),
    setIsDrag: (value) => set({ isDrag: value }),
    setGlobalLoading: (value) => set({ globalLoading: value }),
    setReuploadOption: (value) => set({ reuploadOption: value }),

    // actions
    reset: () => set(initialState),
    fetchPresetGroupList: () => {
      return new Promise((resolve, reject) => {
        getPresetGroupList()
          .then((res) => {
            const { code, data = [], msg } = res;
            if (code === APICode.Success) {
              const list = data.map((item1: any) => ({
                id: item1.id,
                title: item1.title,
                tempList: item1.styles?.map((item2: any) => ({
                  id: item2.id,
                  thumbnail: getResizeImage(
                    item2.effectImg,
                    get().isMobile
                      ? MOBILE_THUMBNAIL_WIDTH
                      : PC_THUMBNAIL_HEIGHT
                  ),
                  resource: { url: item2.effectImg },
                  tag: item2.tag,
                })),
              }));
              set({
                presetGroupList: list,
                activeGroup: list[0],
              });
              resolve();
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    fetchMutiPresetGroupList: () => {
      return new Promise<void>((resolve, reject) => {
        getPresetGroupList("multi")
          .then((res) => {
            const { code, data = [], msg } = res;
            if (code === APICode.Success) {
              const list = data.map((item1: any) => ({
                id: item1.id,
                title: item1.title,
                tempList: item1.styles?.map((item2: any) => ({
                  id: item2.id,
                  thumbnail: getResizeImage(
                    item2.effectImg,
                    get().isMobile
                      ? MOBILE_THUMBNAIL_WIDTH
                      : PC_THUMBNAIL_HEIGHT
                  ),
                  resource: { url: item2.effectImg },
                  tag: item2.tag,
                })),
              }));
              set({
                presetMutiGroupList: list,
                activeMutiGroup: list[0],
              });
              resolve();
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    fetchSign: (extension) => {
      return new Promise((resolve, reject) => {
        getSignUrl(extension)
          .then((res) => {
            const { code, data, msg } = res;
            if (code === APICode.Success) {
              resolve(data);
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    submitFile: (file, url) => {
      return new Promise((resolve, reject) => {
        putFileTo(file, url)
          .then(() => {
            resolve();
          })
          .catch(reject);
      });
    },
    submitTask: (options, featureType) => {
      return new Promise((resolve, reject) => {
        const functionFn =
          featureType === FeatureType.Single ? singleFaceSwap : mutiFaceSwap;

        functionFn(options)
          .then((res) => {
            const { code, data, msg } = res;
            if (code === APICode.Success) {
              resolve(data[0]);
            } else {
              let message = "";
              switch (code) {
                case "101":
                  message = "error_no_face";
                  break;
                case "999":
                  message = "error_server";
                  break;
                case "510":
                  message = "error_no_credits";
                  break;
                case "102":
                  message = "error_img_violation";
                  break;
                case "513":
                  message = "error_no_credits";
                  break;
                case "515":
                  message = "error_no_free";
                  break;
                default:
                  message = msg;
                  break;
              }
              // 埋点
              Analyse.getInstance().startTaskGererateError(false, message);
              const error = new Error(message);
              error.name = code;
              reject(error);
            }
          })
          .catch((err) => {
            // 埋点
            Analyse.getInstance().startTaskGererateError(
              false,
              `submit network failed|${err?.message}|${err?.response?.status}`
            );
            reject(err);
          });
      });
    },
    fetchTask: () => {
      const { task } = get();
      return new Promise((resolve, reject) => {
        getTask(task!.taskId)
          .then((res) => {
            const { code, data, msg } = res;
            if (code === APICode.Success) {
              resolve(data);
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    generate: async (inlineFeatureType) => {
      try {
        const { activeTemp, swapList, featureType } = get();
        let newFeatureType = inlineFeatureType || featureType;
        const { fetchSign, submitFile, submitTask } = get();

        set({ generating: true, pageType: PageType.Generating, task: null });
        const errorFn = () => {
          set({
            pageType: PageType.Create,
            generating: false,
          });
        };

        const signTemp = (value: TTemplate) => {
          if (value.resource.url) {
            // 上传过
            return Promise.resolve(value);
          } else {
            return new Promise<TTemplate>((resolve, reject) => {
              fetchSign(getExtension(value.resource.file!))
                .then((sign) =>
                  resolve({
                    ...value,
                    resource: {
                      ...value.resource,
                      sign,
                    },
                  })
                )
                .catch(reject);
            });
          }
        };

        const uploadTemp = (value: TTemplate) => {
          if (value.resource.url) {
            return Promise.resolve(value);
          } else {
            return new Promise<TTemplate>((resolve, reject) => {
              submitFile(value.resource.file!, value.resource.sign?.uploadUrl!)
                .then(() =>
                  resolve({
                    ...value,
                    resource: {
                      ...value.resource,
                      url: value.resource.sign?.downloadUrl,
                    },
                  })
                )
                .catch(reject);
            });
          }
        };

        const signSwap = (value: TSwap, prop: "source" | "target") => {
          const swapObj = value[prop];

          if (value.source && value.target) {
            if (swapObj?.resource.url) {
              // 上传过了，不签名
              return Promise.resolve(value);
            } else {
              return new Promise<TSwap>((resolve, reject) => {
                fetchSign(getExtension(swapObj?.resource.file!))
                  .then((sign) =>
                    resolve({
                      ...value,
                      [prop]: {
                        ...swapObj,
                        resource: {
                          ...swapObj?.resource,
                          sign,
                        },
                      },
                    })
                  )
                  .catch(reject);
              });
            }
          } else {
            // 没有选择目标图，不签名
            return Promise.resolve(value);
          }
        };

        const uploadSwap = (value: TSwap, prop: "source" | "target") => {
          const swapObj = value[prop];

          if (value.source && value.target) {
            if (swapObj?.resource.url) {
              // 上传过了，不再上传
              return Promise.resolve(value);
            } else {
              return new Promise<TSwap>((resolve, reject) => {
                submitFile(
                  swapObj?.resource.file!,
                  swapObj?.resource.sign?.uploadUrl!
                )
                  .then(() =>
                    resolve({
                      ...value,
                      [prop]: {
                        ...swapObj,
                        resource: {
                          ...swapObj?.resource,
                          url: swapObj?.resource.sign?.downloadUrl,
                        },
                      },
                    })
                  )
                  .catch(reject);
              });
            }
          } else {
            // 没有选择目标图，不上传
            return Promise.resolve(value);
          }
        };
        /**
         * 检测模板的人脸
         * @param value
         */
        const checkTempFace = (value: TTemplate) => {
          return new Promise<TServerDeTectFaceData[]>((resolve, reject) => {
            if (value.resource.url) {
              faceDetect(value.resource.url).then((res) => {
                const { faces } = res;
                resolve(faces as TServerDeTectFaceData[]);
              });
            }
          });
        };

        // 模板签名
        let newActiveTemp;
        try {
          newActiveTemp = await signTemp(activeTemp!);
        } catch (err) {
          // 埋点
          Analyse.getInstance().startTaskGererateError(
            false,
            "target_image_sign_failed"
          );
          set({
            reuploadOption: { show: true, title: t("error_server") },
          });
          throw err;
        }

        // 模板上传
        try {
          newActiveTemp = await uploadTemp(newActiveTemp);
        } catch (err) {
          // 埋点
          Analyse.getInstance().startTaskGererateError(
            false,
            "target_image_upload_failed"
          );
          set({
            reuploadOption: { show: true, title: t("error_server") },
          });
          errorFn();
          throw err;
        }

        if (newFeatureType === FeatureType.Single) {
          // 检测模板人脸
          try {
            const faces = await checkTempFace(newActiveTemp);
            if (faces.length === 0) {
              set({
                activeTemp: null,
                reuploadOption: { show: true, title: t("error_no_face") },
              });
              errorFn();
              Analyse.getInstance().startTaskGererateError(
                false,
                "target_image_no_face"
              );
              return;
            }
            if (faces.length > 1) {
              const isToMuti = await customConfirm(t("confirm_to_muti_title"));
              Analyse.getInstance().startTaskGererateError(
                false,
                "target_image_face_more_one"
              );
              if (isToMuti) {
                get().setFeatureType(FeatureType.Multi);
                get().setPageType(PageType.Create);
                set({ mutiLoading: true, generating: false });
                const swapList: TSwap[] = await faceToSwap(
                  faces,
                  newActiveTemp?.resource?.url ?? ""
                );
                set({ mutiLoading: false, swapList: swapList });
                errorFn();
                return;
              } else {
                set({
                  activeTemp: null,
                });
                errorFn();
                return;
              }
            }
          } catch (err) {
            // 埋点
            Analyse.getInstance().startTaskGererateError(
              false,
              "template:check face failed"
            );
            errorFn();
            throw err;
          }
          const swapTemp = get().swapTemp;
          if (!swapTemp) {
            return;
          }
          let newSwapTemp;
          try {
            newSwapTemp = await signTemp(swapTemp);
            set({ swapTemp: newSwapTemp });
          } catch (err) {
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_image_sign_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            errorFn();
            return;
          }
          if (!newSwapTemp) {
            return;
          }
          try {
            newSwapTemp = await uploadTemp(newSwapTemp);
            set({ swapTemp: newSwapTemp });
          } catch (err) {
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_image_upload_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            errorFn();
          }

          try {
            const faces = await checkTempFace(newSwapTemp);
            if (faces.length === 0) {
              set({
                swapTemp: null,
                reuploadOption: { show: true, title: t("error_no_face") },
              });
              Analyse.getInstance().startTaskGererateError(
                false,
                "face_image_no_face"
              );
              errorFn();
              return;
            }
            if (faces.length > 1) {
              set({
                swapTemp: null,
                reuploadOption: {
                  show: true,
                  title: t("error_many_face"),
                },
              });
              Analyse.getInstance().startTaskGererateError(
                false,
                "face_image_face_more_one"
              );
              errorFn();
              return;
            }
          } catch (err) {
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
          }
        } else if (newFeatureType === FeatureType.Multi) {
          // 模板头像签名
          let newSwapList;
          try {
            newSwapList = await Promise.all(
              swapList.map((item) => signSwap(item, "source"))
            );
          } catch (err) {
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_image_sign_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }
          // 模板头像上传
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => uploadSwap(item, "source"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_image_upload_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }

          // 目标头像签名
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => signSwap(item, "target"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_target_image_sign_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }

          // 目标头像上传
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => uploadSwap(item, "target"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startTaskGererateError(
              false,
              "face_target_image_upload_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }
          set({
            swapList: newSwapList,
          });
        }

        // 构建请求参数
        const taskOptions: TTaskOptions = {
          targetImg: {
            url: newActiveTemp?.resource.url!,
            width: activeTemp?.dimension?.width!,
            height: activeTemp?.dimension?.height!,
          },
        } as TTaskOptions;
        if (newFeatureType === FeatureType.Multi) {
          const newSwapList = get().swapList;
          taskOptions.swapImages = newSwapList
            .filter((item) => item.source && item.target)
            .map((item) => ({
              template_image_url: item.source.resource.url!,
              user_image_url: item.target?.resource.url!,
              w: item.source.dimension?.width!,
              h: item.source.dimension?.height!,
              x: item.source.coordinate?.x!,
              y: item.source.coordinate?.y!,
            }));
        } else if (newFeatureType === FeatureType.Single) {
          const swapTemp = get().swapTemp;
          taskOptions.sourceFaceImg = swapTemp?.resource.url;
        }

        // 请求换脸
        let task;
        try {
          set({
            pageType: PageType.Generating,
            task: null,
            errorCode: "",
            errorDesc: "",
            errorMsg: "",
          });
          task = await submitTask(taskOptions, newFeatureType);
        } catch (err: any) {
          set({
            generating: false,
            pageType: PageType.Create,
          });
          // if(err.name === "102"){
          //   get().clearActiveTemp(); // 需求：生成图片违规时，清空模板
          // }

          throw err;
        }

        // 保存
        set({
          task: { ...task, status: TaskStatus.Waiting },
          activeTemp: newActiveTemp,
          pageType: PageType.Generating,
        });
      } catch (err) {
        set({ generating: false });
        throw err;
      }
    },
    beforeOperate: async (callback) => {
      if (get().generating) {
        toast(t("generating"));
        return;
      }
      callback?.();
    },
    loopTask: async () => {
      try {
        const { fetchTask } = get();

        let loopQuantity = 0;
        let task = await fetchTask();
        while (
          loopQuantity < MAX_LOOP_QUANTITY &&
          [TaskStatus.Waiting, TaskStatus.Generating].includes(task.status)
        ) {
          await sleep(LOOP_INTERVAL);
          task = await fetchTask();
          loopQuantity += 1;
        }

        set({ task, generating: false });
        Analyse.getInstance().startTaskGererate();
        if (task.whetherViolations === true) {
          // 埋点（敏感图）
          Analyse.getInstance().generateTaskCallBack(false, "error_img_violation");
          get().setVoliError("102");
          set({ pageType: PageType.Failed });
          return;
        }
        get().setCommonError(task.status + "");
        switch (task.status) {
          case TaskStatus.Success:
            // 埋点（成功）
            Analyse.getInstance().generateTaskCallBack(true, "");

            set({ pageType: PageType.Result });
            break;
          case TaskStatus.Waiting:
          case TaskStatus.Generating:
            task.status = TaskStatus.FeTimeout;
            set({ task, pageType: PageType.Failed });

            // 埋点（前端超时）
            Analyse.getInstance().generateTaskCallBack(false, "fonted_timeout");

            break;
          case TaskStatus.Timeout:
            // 埋点（后端超时）
            Analyse.getInstance().generateTaskCallBack(false, "server_timeout");
            set({ pageType: PageType.Failed });
            break;
          case TaskStatus.Failed:
            // 埋点（生成失败）
            Analyse.getInstance().generateTaskCallBack(
              false,
              "task_failed_status:" + task.status
            );
            set({ pageType: PageType.Failed });

            break;
          default:
            // 埋点（未知）
            Analyse.getInstance().generateTaskCallBack(
              false,
              "unknown_status:" + task.status
            );
            set({ pageType: PageType.Failed });
            break;
        }
      } catch (err) {
        console.error(err);
        set({ generating: false });
        Analyse.getInstance().generateTaskCallBack(false, "task error:" + err);

        set({ pageType: PageType.Failed });
      }
    },
    showRemoveTip: () => {
      const removeTipShowTimesStr = localStorage.getItem(
        LocalStorageKey.removeTipShowTimes
      );
      if (removeTipShowTimesStr) {
        const removeTipShowTimeList = removeTipShowTimesStr.split(",");
        if (removeTipShowTimeList.length < 3) {
          const getDateComponents = (timestamp: number) => {
            const date = new Date(timestamp);

            const year = date.getFullYear();
            const month = date.getMonth() + 1; // 月份从0开始，因此需要加1
            const dayOfMonth = date.getDate();

            return {
              year: year,
              month: month,
              dayOfMonth: dayOfMonth,
            };
          };

          const lastTimeStr =
            removeTipShowTimeList[removeTipShowTimeList.length - 1];
          const lastTime = Number(lastTimeStr);
          const now = Date.now();
          const lastTimeComponent = getDateComponents(lastTime);
          const nowTimeComponent = getDateComponents(now);
          if (
            nowTimeComponent.year > lastTimeComponent.year ||
            (nowTimeComponent.year === lastTimeComponent.year &&
              nowTimeComponent.month > lastTimeComponent.month) ||
            (nowTimeComponent.year === lastTimeComponent.year &&
              nowTimeComponent.month === lastTimeComponent.month &&
              nowTimeComponent.dayOfMonth > lastTimeComponent.dayOfMonth)
          ) {
            set({ removeTipVisible: true });
            removeTipShowTimeList.push(String(now));
            localStorage.setItem(
              LocalStorageKey.removeTipShowTimes,
              removeTipShowTimeList.join(",")
            );
          }
        }
      } else {
        set({ removeTipVisible: true });
        localStorage.setItem(
          LocalStorageKey.removeTipShowTimes,
          `${Date.now()}`
        );
      }
    },
    logoutCallback: () => {
      set({
        activeTemp: null,
        swapTemp: null,
        swapList: [],
        task: null,
        generating: false,
        mutiLoading: false,
        pageType: PageType.Create,
      });
    },
    preApplyPresetTemp: () => {
      const menu = "type";
      const query = new URLSearchParams(window.location.search);
      const menuValue = query.get(menu);
      const isInFeatureType =
        menuValue === FeatureType.Multi || menuValue === FeatureType.Single;
      if (isInFeatureType) {
        get().setFeatureType(menuValue as FeatureType);
        get().setPageType(PageType.Create);
      }
      query.delete(menu);
      let url = window.location.pathname;
      if ((query as any).size > 0) {
        url += `?${query.toString()}`;
      }
      window.history.replaceState(null, "", url);
    },
    // 进入页面时，根据url参数，应用预设模板
    applyPresetTemp: () => {
      const menu = "type";
      const groupIdName = "style";
      const tempIdName = "id";
      const otherGroupIdName = "soureId";
      const query = new URLSearchParams(window.location.search);
      const groupId = query.get(groupIdName);
      const tempId = query.get(tempIdName);
      const otherGroupId = query.get(otherGroupIdName);
      const menuValue = query.get(menu);
      const featureType = get().featureType;
      const nowGroupId = groupId || otherGroupId;

      const { presetGroupList, chooseTemp, presetMutiGroupList } = get();
      const isSingle = featureType === FeatureType.Single;
      const groupList = isSingle ? presetGroupList : presetMutiGroupList;
      let group, temp;

      group = groupList.find((item) => item.id === nowGroupId);
      groupList.forEach((item1) => {
        const index = item1.tempList.findIndex((item2) => item2.id === tempId);
        if (index > -1) {
          temp = item1.tempList[index];
        }
      });

      if (temp) {
        chooseTemp(temp);
      } else if (group) {
        if (isSingle) {
          set({
            allPresetVisible: true,
            activeGroup: group,
          });
        } else {
          set({
            allPresetVisible: true,
            activeMutiGroup: group,
          });
        }
      }
      query.delete(menu);
      // 清空
      query.delete(groupIdName);
      query.delete(tempIdName);
      let url = window.location.pathname;
      if ((query as any).size > 0) {
        url += `?${query.toString()}`;
      }
      window.history.replaceState(null, "", url);
    },
    showDownloadAd: () => {
      let adDatas: any = localStorage.getItem(LocalStorageKey.adData);
      if (adDatas) {
        try {
          adDatas = JSON.parse(adDatas);
          const adData = adDatas.find((item1: any) => {
            if (item1.type === "export") {
              const index = item1.positionRegex.findIndex((item2: any) =>
                new RegExp(item2.regex).test(window.location.pathname)
              );
              return index !== -1;
            }
            return false;
          });
          if (adData) {
            set({
              downloadAdOption: {
                show: true,
                jumpUrl: adData.jumpUrl,
                cover: adData.cover,
                id: adData.id,
              },
            });
          }
        } catch (err) {
          console.error(err);
        }
      }
    },
    /**
     * 新版本选择模板，不再使用前端人脸检测，现在本地转换格式，客户端点击生成后再上传
     * @param temp
     */
    chooseSwapTemp: async (temp) => {
      Analyse.getInstance().openFaceSwap();
      if (!checkType(temp)) {
        Analyse.getInstance().openFaceSwapCallback(false, "img_format_error");
        toast(t("img_format_error"));
        return;
      }
      if (!checkFileSize(temp)) {
        Analyse.getInstance().openFaceSwapCallback(false, "img_size_error");
        toast(t("img_size_error"));
        return;
      }
      const isLg8192 = await checkImageSize(temp);
      if (!isLg8192) {
        Analyse.getInstance().openFaceSwapCallback(
          false,
          "img_max_slider_error"
        );
        toast(t("img_max_slider_error"));
        return;
      }
      let newFile = temp;
      if (temp instanceof File) {
        newFile = (await zipFile(temp)).file;
      }
      const isCustom = typeof temp === "string" || temp instanceof File;
      if (isCustom) {
        if (typeof temp === "string") {
          set({
            swapTemp: {
              id: getRandomId(),
              thumbnail: temp,
              resource: {
                url: temp,
              },
            },
          });
        } else {
          const blobUrl = URL.createObjectURL(newFile as File);
          set({
            swapTemp: {
              id: getRandomId(),
              thumbnail: blobUrl,
              resource: {
                file: newFile as File,
                blobUrl,
              },
            },
          });
        }
      } else {
        set({ swapTemp: { ...temp } });
      }
      Analyse.getInstance().openFaceSwapCallback(true, "");
    },
    chooseFaceSwapTemp: async (temp: any) => {
      if (!checkType(temp)) {
        toast(t("img_format_error"));
        return;
      }
    },

    chooseTemp: async (temp) => {
      Analyse.getInstance().openTarget();
      let newFile = temp;
      if (!checkType(temp)) {
        toast(t("img_format_error"));
        Analyse.getInstance().openTargetCallback(false, "img_format_error");
        return;
      }

      if (!checkFileSize(temp)) {
        toast(t("img_size_error"));
        Analyse.getInstance().openTargetCallback(false, "img_size_error");
        return;
      }
      const isLg8192 = await checkImageSize(temp);
      if (!isLg8192) {
        Analyse.getInstance().openTargetCallback(false, "img_max_slider_error");
        toast(t("img_max_slider_error"));
        return;
      }
      const initFeatureType = get().featureType;
      const mutiLoading = get().mutiLoading;
      if (initFeatureType === FeatureType.Multi && mutiLoading) {
        return;
      }
      if (initFeatureType === FeatureType.Multi) {
        set({ mutiLoading: true });
      }
      if (temp instanceof File) {
        newFile = (await zipFile(temp)).file;
      }
      const nowFeatureType = get().featureType;
      if (nowFeatureType !== initFeatureType) {
        set({ mutiLoading: false });
        return;
      }
      const isCustom = typeof newFile === "string" || newFile instanceof File;
      try {
        // const detectImg = isCustom ? temp : temp.resource.url!;
        if (isCustom) {
          if (typeof newFile === "string") {
            set({
              activeTemp: {
                id: getRandomId(),
                thumbnail: newFile,
                resource: {
                  url: newFile,
                },
              },
            });
          } else {
            const blobUrl = URL.createObjectURL(newFile as File);
            console.log(newFile);
            set({
              activeTemp: {
                id: getRandomId(),
                thumbnail: blobUrl,
                resource: {
                  file: newFile as File,
                  blobUrl,
                },
              },
            });
          }
          const nowFeatureType = get().featureType;
          if (nowFeatureType !== initFeatureType) {
            return;
          }
          await imageLoader(newFile as File).then((img) => {
            const { activeTemp } = get();
            const nowFeatureType = get().featureType;
            if (nowFeatureType !== initFeatureType) {
              return;
            }
            if (!activeTemp) {
              return;
            }
            set({
              activeTemp: {
                ...activeTemp,
                id: activeTemp?.id ?? getRandomId(),
                dimension: {
                  width: img.width,
                  height: img.height,
                },
              } as TTemplate,
            });
          });
        } else {
          set({ activeTemp: { ...(newFile as TTemplate) } });
          if (newFile && (newFile as TTemplate).resource.url) {
            await imageLoader((newFile as TTemplate).resource.url || "").then(
              (img) => {
                const { activeTemp } = get();
                if (!activeTemp) {
                  return;
                }
                set({
                  activeTemp: {
                    ...activeTemp,
                    id: activeTemp?.id ?? getRandomId(),
                    dimension: {
                      width: img.width,
                      height: img.height,
                    },
                  } as TTemplate,
                });
              }
            );
          }
        }
        const nowFeatureType = get().featureType;
        if (nowFeatureType !== initFeatureType) {
          set({ mutiLoading: false });
          return;
        }
        // 如果是多人脸，需要执行一次人脸检测
        if (get().featureType === FeatureType.Multi) {
          const { submitFile, fetchSign, activeTemp } = get();
          // if (mutiLoading) {
          //   return;
          // }
          const signTemp = (value: TTemplate) => {
            if (value.resource.url) {
              // 上传过
              return Promise.resolve(value);
            } else {
              return new Promise<TTemplate>((resolve, reject) => {
                fetchSign(getExtension(value.resource.file!))
                  .then((sign) =>
                    resolve({
                      ...value,
                      resource: {
                        ...value.resource,
                        sign,
                      },
                    })
                  )
                  .catch(reject);
              });
            }
          };

          const uploadTemp = (value: TTemplate) => {
            if (value.resource.url) {
              return Promise.resolve(value);
            } else {
              return new Promise<TTemplate>((resolve, reject) => {
                submitFile(
                  value.resource.file!,
                  value.resource.sign?.uploadUrl!
                )
                  .then(() =>
                    resolve({
                      ...value,
                      resource: {
                        ...value.resource,
                        url: value.resource.sign?.downloadUrl,
                      },
                    })
                  )
                  .catch(reject);
              });
            }
          };
          const checkTempFace = (value: TTemplate) => {
            return new Promise<TServerDeTectFaceData[]>((resolve, reject) => {
              if (value.resource.url) {
                faceDetect(value.resource.url).then((res) => {
                  const { faces } = res;
                  if (faces) {
                    resolve(faces as TServerDeTectFaceData[]);
                  } else {
                    reject(new Error("face not found"));
                  }
                });
              }
            });
          };

          let newActiveTemp = activeTemp;
          if (!newActiveTemp) {
            return;
          }
          get().setMutiLoading(true);
          const end = () => {
            get().setMutiLoading(false);
          };
          try {
            newActiveTemp = await signTemp(newActiveTemp);
          } catch (err) {
            Analyse.getInstance().openTargetCallback(false, "target_sign_fail");
            end();
            return;
          }

          // 模板上传
          try {
            newActiveTemp = await uploadTemp(newActiveTemp);
          } catch (err) {
            Analyse.getInstance().openTargetCallback(
              false,
              "target_upload_fail"
            );
            end();
            return;
          }
          set({
            activeTemp: newActiveTemp,
          });
          // 检测模板人脸
          try {
            const faces = await checkTempFace(newActiveTemp);
            if (faces.length > 0) {
              const swapList: TSwap[] = await faceToSwap(
                faces,
                newActiveTemp?.resource?.url ?? ""
              );
              const { activeTemp } = get();
              if (activeTemp) {
                set({
                  swapList: swapList,
                });
              } else {
                set({
                  swapList: [],
                });
              }
            } else {
              Analyse.getInstance().openTargetCallback(false, "target_no_face");
              set({
                activeTemp: null,
                reuploadOption: { show: true, title: t("error_no_face") },
                swapList: [],
              });
            }
            end();
          } catch (err) {
            Analyse.getInstance().openTargetCallback(
              false,
              `unkown_error:${
                typeof err === "string" ? err : (err as Error).message
              }`
            );
            end();
          }
        }
        Analyse.getInstance().openTargetCallback(true, "");
      } catch (err) {
        Analyse.getInstance().openTargetCallback(
          false,
          `unkown_error:${
            typeof err === "string" ? err : (err as Error).message
          }`
        );
        console.error(err);
        set({ globalLoading: false, mutiLoading: false });
      }
    },
    clearTask: () => {
      set({
        task: null,
      });
    },
    clearActiveTemp: () => {
      set({
        activeTemp: null,
        swapList: [],
      });
    },
    clearSwapTemp: () => {
      set({
        swapTemp: null,
      });
    },
    chooseTarget: async (swap) => {
      Analyse.getInstance().openFaceSwap();
      let [file] = await selectFile({ accept: TYPE_LIMIT_ACCEPT });

      if (!checkType(file)) {
        toast(t("img_format_error"));
        Analyse.getInstance().openFaceSwapCallback(false, "img_format_error");

        return;
      }

      if (isTiff(file)) {
        file = await tiff2png(file);
      }

      if (isHeic(file)) {
        file = await heic2jpeg(file);
      }

      if (!checkFileSize(file)) {
        toast(t("img_size_error"));
        Analyse.getInstance().openFaceSwapCallback(false, "img_size_error");
        return;
      }

      const isLg8192 = await checkImageSize(file);
      if (!isLg8192) {
        Analyse.getInstance().openFaceSwapCallback(
          false,
          "img_max_slider_error"
        );
        toast(t("img_max_slider_error"));
        return;
      }

      const newFile = (await zipFile(file)).file;
      const { swapList } = get();
      const findSwap = swapList.find((item) => item.id === swap.id);
      if (!findSwap) {
        return;
      }
      try {
        findSwap.loading = true;
        set({ swapList: [...swapList] });
        const sign = await fetchSign(getExtension(newFile));
        await submitFile(newFile, sign.uploadUrl || sign.backupUploadUrl || "");
        const img = await imageLoader(newFile);
        let facesResut: TServerDeTectData = await faceDetect(
          sign.downloadUrl || ""
        );
        let code: FaceDetectRes = FaceDetectRes.Unknown;
        let detections: any[] = facesResut.faces.map((item) => {
          const [x1, y1, x2, y2] = item.bbox;
          const width = x2 - x1;
          const height = y2 - y1;
          const newFace = expandFace(
            {
              width: img.width,
              height: img.height,
            },
            {
              x: x1,
              y: y1,
            },
            {
              width,
              height,
            }
          );
          return {
            expandBox: {
              x: newFace.coordinate.x,
              y: newFace.coordinate.y,
              width: newFace.dimensions.width,
              height: newFace.dimensions.height,
            },
          };
        });

        if (facesResut.faces.length === 0) {
          code = FaceDetectRes.NoFace;
        } else if (facesResut.faces.length === 1) {
          code = FaceDetectRes.Success;
        } else if (facesResut.faces.length > 1) {
          code = FaceDetectRes.TooManyFace;
        }
        switch (code) {
          case FaceDetectRes.NoFace:
            findSwap.loading = false;
            Analyse.getInstance().openFaceSwapCallback(
              false,
              "face_image_no_face"
            );
            set({
              swapList: [...swapList],
              reuploadOption: { show: true, title: t("error_no_face") },
            });
            break;
          // case FaceDetectRes.TooSmall:
          //   set({
          //     reuploadOption: { show: true, title: t("face_result_too_small") },
          //   });
          //   break;
          // case FaceDetectRes.TooBig:
          //   set({
          //     reuploadOption: { show: true, title: t("face_result_too_big") },
          //   });
          //   break;
          case FaceDetectRes.Unknown:
            set({ reuploadOption: { show: true, title: t("unknown_error") } });
            break;
          case FaceDetectRes.Success:
            Analyse.getInstance().openFaceSwapCallback(true, "");
            const detection = detections[0];
            const targetHead: File = await cropImage(
              newFile,
              detection.expandBox.x,
              detection.expandBox.y,
              detection.expandBox.width,
              detection.expandBox.height
            );
            findSwap.loading = false;
            findSwap!.target = {
              resource: {
                file: targetHead,
                blobUrl: URL.createObjectURL(targetHead),
              },
              dimension: {
                width: detection.expandBox.width,
                height: detection.expandBox.height,
              },
              coordinate: {
                x: detection.expandBox.x,
                y: detection.expandBox.y,
              },
            };

            set({
              swapList: [...swapList],
            });
            break;
          case FaceDetectRes.TooManyFace:
            findSwap.loading = false;

            const faceDetectionList: TFaceDetection[] = await Promise.all(
              detections.map((detection) =>
                cropImage(
                  newFile,
                  detection.expandBox.x,
                  detection.expandBox.y,
                  detection.expandBox.width,
                  detection.expandBox.height
                ).then((res) => ({ ...detection, face: res }))
              )
            );

            set({
              swapList: [...swapList],
              multiFaceSelectOptions: {
                show: true,
                faceDetectionList,
                onSelect: (faceDetection) => {
                  Analyse.getInstance().openFaceSwapCallback(true, "");
                  const { face, expandBox } = faceDetection;
                  findSwap!.target = {
                    resource: {
                      file: face,
                      blobUrl: URL.createObjectURL(face!),
                    },
                    dimension: {
                      width: expandBox.width,
                      height: expandBox.height,
                    },
                    coordinate: {
                      x: expandBox.x,
                      y: expandBox.y,
                    },
                  };

                  set({
                    swapList: [...swapList],
                  });
                },
              },
            });
            break;
          default:
            break;
        }
      } catch (err) {
        console.error(err);
        Analyse.getInstance().openFaceSwapCallback(
          false,
          `unkown_error:${
            typeof err === "string" ? err : (err as Error).message
          }`
        );
        set({ globalLoading: false });
        findSwap!.loading = false;
        set({
          swapList: [...swapList],
        });
      }
    },
    clearTarget: (swap: TSwap) => {
      const { swapList } = get();
      const findSwap = swapList.find((item) => item.id === swap.id);
      findSwap!.target = undefined;
      set({ swapList: [...swapList] });
    },

    uploadFile: async (file: File) => {
      // 上传图片并检测人脸
      const { fetchSign, submitFile } = get();
      const signTemp = () => {
        return new Promise<TSign>((resolve, reject) => {
          fetchSign(getExtension(file))
            .then((sign) => resolve(sign))
            .catch(reject);
        });
      };

      const uploadTemp = (file: File, sign: TSign) => {
        return new Promise<Boolean>((resolve, reject) => {
          submitFile(file!, sign?.uploadUrl!)
            .then(() => resolve(true))
            .catch(reject);
        });
      };

      const sign = await signTemp();
      const upload = await uploadTemp(file, sign);
      if (upload) {
        return sign;
      }
      return null;
    },

    faceDetect: async (sign: TSign) => {
      if (!sign || !sign.downloadUrl) {
        throw Promise.reject(new Error("sign is null"));
      }
      const data = await faceDetect(sign.downloadUrl);
      return data;
    },
  })
);

export default useCommonStore;
